import React, { useState } from 'react';
import { Check, X, Bell, Calendar, Beaker, Trophy, Info, Trash2 } from 'lucide-react';
import { Notification } from '../../types/notifications';
import { 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  deleteNotification,
  deleteAllNotifications
} from '../../lib/notificationStore';

interface NotificationPanelProps {
  notifications: Notification[];
  onClose: () => void;
  onNotificationsUpdate: (notifications: Notification[]) => void;
}

export function NotificationPanel({ notifications, onClose, onNotificationsUpdate }: NotificationPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'starter_feeding':
        return Beaker;
      case 'session_autolyse':
      case 'session_kneading':
      case 'session_shaping':
      case 'session_baking':
      case 'session_completed':
        return Calendar;
      case 'challenge_new':
      case 'challenge_update':
        return Trophy;
      default:
        return Info;
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      return 'just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setLoading(true);
      setError(null);
      await markAllNotificationsAsRead();
      // Update local state immediately
      const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
      onNotificationsUpdate(updatedNotifications);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to mark notifications as read');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setLoading(true);
      setError(null);
      await deleteAllNotifications();
      // Update local state immediately
      onNotificationsUpdate([]);
    } catch (err) {
      console.error('Error deleting all notifications:', err);
      setError('Failed to delete notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await markNotificationAsRead(notification.id);
        // Update local state immediately
        const updatedNotifications = notifications.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        );
        onNotificationsUpdate(updatedNotifications);
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }

    // Handle navigation if route is provided
    if (notification.data?.route) {
      // Navigation logic can be added here if needed
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      await deleteNotification(notificationId);
      // Update local state immediately
      const updatedNotifications = notifications.filter(n => n.id !== notificationId);
      onNotificationsUpdate(updatedNotifications);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  };

  const renderNotificationContent = (notification: Notification) => {
    if (notification.type === 'starter_feeding') {
      return (
        <>
          <p className={`text-sm font-medium ${notification.read ? 'text-gray-900' : 'text-blue-900'}`}>
            {notification.title}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            <span className="font-medium text-amber-600">{notification.data?.starterName}</span>
            {' needs feeding'}
          </p>
        </>
      );
    }
    
    if (notification.type.startsWith('session_')) {
      // Get milestone type from the notification type
      const milestoneType = notification.type.replace('session_', '');
      const sessionName = notification.data?.sessionName || 'Your bread';
      
      let icon;
      let iconColor;
      
      switch (milestoneType) {
        case 'autolyse':
          icon = '🌾';
          iconColor = 'text-yellow-600';
          break;
        case 'kneading':
          icon = '👐';
          iconColor = 'text-amber-600';
          break;
        case 'shaping':
          icon = '🍞';
          iconColor = 'text-amber-700';
          break;
        case 'baking':
          icon = '🔥';
          iconColor = 'text-red-600';
          break;
        default:
          icon = '⏰';
          iconColor = 'text-blue-600';
      }
      
      return (
        <>
          <p className={`text-sm font-medium ${notification.read ? 'text-gray-900' : 'text-blue-900'}`}>
            {notification.title}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            <span className="font-medium text-amber-600">{sessionName}</span>
            {' - '}{notification.message}
          </p>
        </>
      );
    }

    return (
      <>
        <p className={`text-sm font-medium ${notification.read ? 'text-gray-900' : 'text-blue-900'}`}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-600 mt-0.5">
          {notification.message}
        </p>
      </>
    );
  };

  return (
    <div className="fixed top-16 right-0 w-96 max-w-[90vw] sm:w-96 bg-[#FCF7F5] border border-gray-200 shadow-lg overflow-hidden z-[150]">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-gray-600" />
          <h2 className="hidden sm:block font-medium">Notifications</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleMarkAllRead}
            disabled={loading || notifications.every(n => n.read)}
            className={`text-sm ${
              loading || notifications.every(n => n.read)
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mark all as read
          </button>
          <button
            onClick={handleDeleteAll}
            disabled={loading || notifications.length === 0}
            className={`text-sm ${
              loading || notifications.length === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-red-600 hover:text-red-700'
            }`}
          >
            Delete all
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-100 text-red-600 text-sm">
          {error}
        </div>
      )}

      <div className="max-h-[calc(80vh-8rem)] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.type);
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 flex gap-3 cursor-pointer transition-colors ${
                    notification.read ? 'bg-white' : 'bg-blue-50'
                  } hover:bg-gray-50`}
                >
                  <div className={`flex-shrink-0 mt-1 ${
                    notification.read ? 'text-gray-400' : 'text-blue-500'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {renderNotificationContent(notification)}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTime(notification.createdAt)}
                    </p>
                  </div>

                  <div className="flex-shrink-0 flex items-start gap-2">
                    {!notification.read && (
                      <div className="w-2 h-2 mt-2 bg-blue-500 rounded-full" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(notification.id);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}